import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactApexChart from "react-apexcharts";
import Swal from "sweetalert2";

import PageTitle from "../../../layouts/PageTitle";
import { selectUserRole } from "../../../../store/authSlice";
import {
  appApi,
  useGetNcsIncentiveQuery,
  useGetNcsIncentiveNewConnectionsQuery,
  useGetNcsIncentivePlansQuery,
  useGetNcsIncentiveConfigQuery,
} from "../../../../store/appApi";
import {
  setIncentivePlanPriceApi,
  updateIncentiveConfigApi,
} from "../../../../services/netplusCustomerStatus";
import { PartnerPicker, panelClass, fieldClass, labelClass, fmtDateInput } from "./config";
import { exportIncentiveExcel } from "./exportIncentiveExcel";

const inr = (n) =>
  "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";
const thisMonth = () => fmtDateInput().slice(0, 7);

const VIEWS = [
  ["day", "Daily"],
  ["month", "Monthly"],
  ["year", "Yearly"],
];

const Kpi = ({ label, value, sub, tone = "text-gray-900" }) => (
  <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div className={`mt-1 text-xl font-bold tabular-nums ${tone}`}>{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-gray-500">{sub}</div>}
  </div>
);

const Delta = ({ cur, prev, label }) => {
  if (prev == null) return null;
  if (!prev) return <span className="text-gray-400">{label}: no earlier data</span>;
  const pct = ((cur - prev) / prev) * 100;
  const up = pct >= 0;
  return (
    <span className={up ? "text-emerald-600" : "text-rose-600"}>
      {up ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}% {label}
    </span>
  );
};

// The customers behind one partner's "New" count.
const NewConnectionsModal = ({ partner, from, to, partnerCode, periodLabel, onClose }) => {
  const { data, isFetching } = useGetNcsIncentiveNewConnectionsQuery({
    from,
    to,
    partner,
    partnerCode,
  });
  const rows = data?.customers || [];

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className="relative flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-3.5">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              New connections — {partner}
            </h3>
            <div className="mt-0.5 text-[11px] text-gray-500">
              {periodLabel}
              {!isFetching && ` · ${rows.length} customer${rows.length === 1 ? "" : "s"}`}
            </div>
          </div>
          <button
            onClick={onClose}
            className="-mr-1 rounded-lg p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-3 py-2 text-left">ID</th>
                <th className="px-3 py-2 text-left">Phone</th>
                <th className="px-3 py-2 text-left">Plan</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-right">Value (incl GST)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-400">Loading…</td>
                </tr>
              )}
              {!isFetching && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                    No new connections in this period.
                  </td>
                </tr>
              )}
              {!isFetching &&
                rows.map((c) => (
                  <tr key={c.customerKey}>
                    <td className="px-4 py-2 font-medium text-gray-800">{c.name || "—"}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">{c.userId}</td>
                    <td className="px-3 py-2 tabular-nums text-gray-600">{c.phone || "—"}</td>
                    <td className="max-w-[260px] break-all px-3 py-2 text-xs text-gray-700">
                      {c.plan || "—"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-500">
                      {c.createDate ? String(c.createDate).slice(0, 10) : "—"}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums">
                      {c.amountInclGst != null ? inr(c.amountInclGst) : "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
            {!isFetching && rows.length > 0 && (
              <tfoot className="sticky bottom-0 bg-gray-50 text-sm font-semibold">
                <tr>
                  <td colSpan={5} className="px-4 py-2 text-right text-gray-600">Total</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {inr(rows.reduce((a, c) => a + (c.amountInclGst || 0), 0))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Excel download — monthly (Jan–Dec of a chosen year) or yearly report, each
// with recharge and new connection count + value. Uses the dashboard's partner.
const ExcelExport = ({ partner, defaultYear }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(defaultYear);
  const [busy, setBusy] = useState(null);

  useEffect(() => setYear(defaultYear), [defaultYear]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const reqs = [];
      const fetchView = (args) => {
        const r = dispatch(appApi.endpoints.getNcsIncentive.initiate(args));
        reqs.push(r);
        return r.unwrap();
      };
      try {
        const label = partner === "ALL" ? "All partners" : partner;
        if (kind === "month") {
          const data = await fetchView({ view: "month", year: String(year), partnerCode: partner });
          await exportIncentiveExcel(kind, data, label);
        } else {
          // Yearly summary + the month-wise breakdown of each of those years.
          const data = await fetchView({ view: "year", partnerCode: partner });
          const years = (data?.series || []).map((s) => s.label);
          const monthly = await Promise.all(
            years.map((y) => fetchView({ view: "month", year: y, partnerCode: partner })),
          );
          await exportIncentiveExcel(kind, data, label, monthly);
        }
      } finally {
        reqs.forEach((r) => r.unsubscribe());
      }
      setOpen(false);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Could not download",
        text: e?.data?.message || errMsg(e),
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700"
      >
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
          <path d="M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5M4 15h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Download Excel
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[1100]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-[1101] mt-2 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Monthly report
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="number"
                className={`${fieldClass} w-24`}
                style={{ backgroundColor: "#fff", color: "#1f2937" }}
                value={year}
                onChange={(e) => setYear(e.target.value)}
              />
              <button
                type="button"
                disabled={!!busy || !/^\d{4}$/.test(String(year))}
                onClick={() => download("month")}
                className="flex-1 rounded-lg border border-emerald-600 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              >
                {busy === "month" ? "Preparing…" : "Jan–Dec, month & partner-wise"}
              </button>
            </div>
            <div className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Yearly report
            </div>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => download("year")}
              className="mt-1.5 w-full rounded-lg border border-emerald-600 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              {busy === "year" ? "Preparing…" : "Last 5 years, year/month & partner-wise"}
            </button>
            <p className="mt-2.5 text-[11px] leading-snug text-gray-500">
              Recharge and new connection count, value (ex- and incl. GST) and
              incentive, with a partner-wise sheet.{" "}
              {partner === "ALL" ? "All partners." : `Partner ${partner} only.`}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// Partner-wise bifurcation: recharge and new connection count + value per
// partner; expand a partner to see it per day / month / year (current view).
const PERIOD_WORD = { day: "day", month: "month", year: "year" };
const PartnerTable = ({ data, view, onNewClick }) => {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (code) =>
    setOpen((prev) => {
      const n = new Set(prev);
      if (n.has(code)) n.delete(code);
      else n.add(code);
      return n;
    });
  const seriesOf = useMemo(
    () => new Map((data?.partnerSeries || []).map((p) => [p.partnerCode, p.series])),
    [data],
  );
  // "5 Oct 2026" (daily), "Mar 2026" (monthly), "2025" (yearly)
  const periodLabel = (l) => (view === "year" ? l : `${l} ${data?.currentLabel || ""}`);

  return (
    <section className={`${panelClass} overflow-x-auto`}>
      <h3 className="px-4 pt-3 text-sm font-semibold text-gray-800">
        By partner
        <span className="ml-2 text-[11px] font-normal text-gray-400">
          click a partner for {PERIOD_WORD[view]}-wise split
        </span>
      </h3>
      <table className="min-w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-gray-500">
          <tr>
            <th rowSpan={2} className="px-4 py-2 text-left align-bottom">Partner</th>
            <th colSpan={2} className="border-b border-gray-100 px-3 pt-2 text-center">Recharge</th>
            <th colSpan={2} className="border-b border-gray-100 px-3 pt-2 text-center">New connection</th>
            <th rowSpan={2} className="px-4 py-2 text-right align-bottom">Incentive</th>
          </tr>
          <tr>
            <th className="px-3 py-1 text-right">Count</th>
            <th className="px-3 py-1 text-right">Value</th>
            <th className="px-3 py-1 text-right">Count</th>
            <th className="px-3 py-1 text-right">Value</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(data?.partners || []).map((p) => {
            const isOpen = open.has(p.partnerCode);
            const rows = (seriesOf.get(p.partnerCode) || []).filter((s) => s.count > 0);
            return (
              <React.Fragment key={p.partnerCode}>
                <tr className="cursor-pointer hover:bg-gray-50" onClick={() => toggle(p.partnerCode)}>
                  <td className="whitespace-nowrap px-4 py-2 font-medium text-gray-800">
                    <span className="mr-1.5 inline-block w-3 text-[10px] text-gray-400">
                      {isOpen ? "▼" : "▶"}
                    </span>
                    {p.partnerCode}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.recharge.count}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">{inr(p.recharge.base)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {p.newConn.count > 0 ? (
                      <button
                        type="button"
                        className="font-semibold text-cyan-700 underline decoration-dotted underline-offset-2 hover:text-cyan-900"
                        title="View new connection customers"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNewClick(p.partnerCode);
                        }}
                      >
                        {p.newConn.count}
                      </button>
                    ) : (
                      p.newConn.count
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">{inr(p.newConn.base)}</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">{inr(p.incentive)}</td>
                </tr>
                {isOpen &&
                  (rows.length ? (
                    rows.map((s) => (
                      <tr key={s.label} className="bg-cyan-50/40 text-xs">
                        <td className="whitespace-nowrap py-1.5 pl-10 pr-4 text-gray-600">{periodLabel(s.label)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{s.recharge.count}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{inr(s.recharge.base)}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{s.newConn.count}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-600">{inr(s.newConn.base)}</td>
                        <td className="px-4 py-1.5 text-right font-semibold tabular-nums">{inr(s.incentive)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr className="bg-cyan-50/40 text-xs">
                      <td colSpan={6} className="py-1.5 pl-10 text-gray-400">No activity in this period.</td>
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <p className="px-4 pb-3 pt-1 text-[10px] text-gray-400">Values are ex-GST.</p>
    </section>
  );
};

// ═══════════════════════════════ Dashboard ══════════════════════════════════
const Dashboard = () => {
  const [view, setView] = useState("day");
  const [month, setMonth] = useState(thisMonth());
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [partner, setPartner] = useState("ALL");
  const [newConnPartner, setNewConnPartner] = useState(null);

  const { data, isFetching } = useGetNcsIncentiveQuery({
    view,
    month: view === "day" ? month : undefined,
    year: view === "month" ? year : undefined,
    partnerCode: partner,
  });

  const series = useMemo(() => data?.series || [], [data]);
  const t = data?.totals;
  const pt = data?.previousTotals;
  const labels = series.map((s) => s.label);
  const hasPrev = view !== "year";

  const compare = {
    series: [
      { name: data?.currentLabel || "This period", data: series.map((s) => Math.round(s.incentive)) },
      ...(hasPrev
        ? [{ name: data?.previousLabel || "Previous", data: series.map((s) => Math.round(s.previous?.incentive || 0)) }]
        : []),
    ],
    options: {
      chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit" },
      colors: ["#0891b2", "#cbd5e1"],
      plotOptions: { bar: { columnWidth: "70%", borderRadius: 2 } },
      dataLabels: { enabled: false },
      xaxis: { categories: labels, labels: { style: { fontSize: "10px" } } },
      yaxis: { labels: { formatter: (v) => inr(v) } },
      tooltip: { y: { formatter: (v) => inr(v) } },
      grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
      legend: { position: "top" },
    },
  };

  const split = {
    series: [
      { name: "Recharge", data: series.map((s) => Math.round(s.recharge.incentive)) },
      { name: "New connection", data: series.map((s) => Math.round(s.newConn.incentive)) },
    ],
    options: {
      chart: { type: "area", stacked: true, toolbar: { show: false }, fontFamily: "inherit" },
      colors: ["#6366f1", "#10b981"],
      stroke: { curve: "smooth", width: 2 },
      fill: { type: "gradient", gradient: { opacityFrom: 0.5, opacityTo: 0.1 } },
      dataLabels: { enabled: false },
      xaxis: { categories: labels, labels: { style: { fontSize: "10px" } } },
      yaxis: { labels: { formatter: (v) => inr(v) } },
      tooltip: { y: { formatter: (v) => inr(v) } },
      grid: { borderColor: "#eef2f7", strokeDashArray: 4 },
      legend: { position: "top" },
    },
  };

  return (
    <>
      <div className={`${panelClass} mb-4 flex flex-wrap items-end gap-3 px-5 py-3`}>
        <div className="inline-flex rounded-full bg-gray-100 p-1">
          {VIEWS.map(([k, l]) => (
            <button
              key={k}
              onClick={() => setView(k)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold ${
                view === k ? "bg-white text-cyan-700 shadow" : "text-gray-500"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        {view === "day" && (
          <div>
            <span className={labelClass}>Month</span>
            <input
              type="month"
              className={`${fieldClass} w-44`}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>
        )}
        {view === "month" && (
          <div>
            <span className={labelClass}>Year</span>
            <input
              type="number"
              className={`${fieldClass} w-28`}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          {isFetching && <span className="text-xs text-gray-400">Loading…</span>}
          <PartnerPicker allowAll value={partner} onChange={setPartner} />
          <ExcelExport partner={partner} defaultYear={view === "day" ? month.slice(0, 4) : year} />
        </div>
      </div>

      {data?.unpriced?.length > 0 && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          {data.unpriced.reduce((a, b) => a + b.count, 0)} event(s) on plans with no
          price are counted as ₹0 — set them on the <b>Plan prices</b> tab.
        </div>
      )}

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label={`Total incentive @ ${data?.incentivePct ?? 14}%`}
          value={inr(t?.incentive)}
          tone="text-cyan-700"
          sub={
            <>
              <Delta cur={t?.incentive} prev={pt?.incentive} label={`vs ${data?.previousLabel}`} />
            </>
          }
        />
        <Kpi
          label="Recharge incentive"
          value={inr(t?.recharge.incentive)}
          sub={`${t?.recharge.count || 0} recharges · base ${inr(t?.recharge.base)}`}
        />
        <Kpi
          label="New connection incentive"
          value={inr(t?.newConn.incentive)}
          sub={`${t?.newConn.count || 0} connections · base ${inr(t?.newConn.base)}`}
        />
        <Kpi
          label="Business value (ex-GST)"
          value={inr(t?.base)}
          sub={`${t?.count || 0} events`}
        />
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <section className={`${panelClass} p-4`}>
          <h3 className="mb-2 text-sm font-semibold text-gray-800">
            {hasPrev ? "Comparison with previous period" : "Incentive by year"}
          </h3>
          <ReactApexChart type="bar" height={280} series={compare.series} options={compare.options} />
        </section>
        <section className={`${panelClass} p-4`}>
          <h3 className="mb-2 text-sm font-semibold text-gray-800">
            Recharge vs new connection
          </h3>
          <ReactApexChart type="area" height={280} series={split.series} options={split.options} />
        </section>
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <PartnerTable
          data={data}
          view={view}
          onNewClick={(code) => setNewConnPartner(code)}
        />
        <section className={`${panelClass} overflow-x-auto`}>
          <h3 className="px-4 pt-3 text-sm font-semibold text-gray-800">Top plans</h3>
          <table className="min-w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Plan</th>
                <th className="px-3 py-2 text-right">Amt (incl GST)</th>
                <th className="px-3 py-2 text-right">Events</th>
                <th className="px-4 py-2 text-right">Incentive</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.plans || []).map((p) => (
                <tr key={p.plan}>
                  <td className="max-w-[260px] truncate px-4 py-2 text-xs text-gray-700" title={p.plan}>
                    {p.plan}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{inr(p.amountInclGst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.count}</td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">{inr(p.incentive)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <p className="text-[11px] text-gray-400">
        Incentive = recharge amount excluding GST × {data?.incentivePct ?? 14}%. Recharges
        are counted from the daily-sheet upload history (which begins when uploads
        started); new connections use each customer&apos;s create date.
        {data?.estimatedPlans ? ` ${data.estimatedPlans} plan(s) are priced from the base price in their name — confirm them on the Plan prices tab.` : ""}
      </p>

      {newConnPartner && (
        <NewConnectionsModal
          partner={newConnPartner}
          from={data?.from}
          to={data?.to}
          partnerCode={partner}
          periodLabel={`${data?.from} to ${data?.to}`}
          onClose={() => setNewConnPartner(null)}
        />
      )}
    </>
  );
};

// ═══════════════════════════════ Plan prices ════════════════════════════════
const SRC = {
  override: ["bg-violet-100 text-violet-700", "set by you"],
  parsed: ["bg-emerald-100 text-emerald-700", "from plan name"],
  estimated: ["bg-amber-100 text-amber-700", "estimated"],
  none: ["bg-rose-100 text-rose-700", "no price"],
};

const PlanPrices = () => {
  const dispatch = useDispatch();
  const { data, isFetching } = useGetNcsIncentivePlansQuery();
  const [q, setQ] = useState("");
  const [only, setOnly] = useState("all");
  const [draft, setDraft] = useState({});
  const plans = (data?.plans || []).filter(
    (p) =>
      p.plan.toLowerCase().includes(q.trim().toLowerCase()) &&
      (only === "all" || (only === "check" ? p.source === "estimated" || p.source === "none" : p.source === only)),
  );

  const save = async (p, val) => {
    try {
      await setIncentivePlanPriceApi(p.plan, val === "" ? null : Number(val));
      dispatch(appApi.util.invalidateTags(["NetplusIncentive"]));
      setDraft((d) => {
        const n = { ...d };
        delete n[p.plan];
        return n;
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Could not save", text: errMsg(e) });
    }
  };

  return (
    <section className={`${panelClass} overflow-x-auto`}>
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-5 py-3">
        <input
          className={`${fieldClass} w-64`}
          style={{ backgroundColor: "#fff", color: "#1f2937" }}
          placeholder="Search plan…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className={`${fieldClass} w-48`}
          style={{ backgroundColor: "#fff", color: "#1f2937" }}
          value={only}
          onChange={(e) => setOnly(e.target.value)}
        >
          <option value="all">All plans</option>
          <option value="check">Needs checking (estimated / none)</option>
          <option value="parsed">From plan name</option>
          <option value="override">Set by you</option>
        </select>
        <span className="text-xs text-gray-400">
          The amount is read from the plan name. Type the amount <b>including GST</b> to
          override; clear it to go back to the plan-name value.
        </span>
      </div>
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
          <tr>
            <th className="px-4 py-2 text-left">Plan</th>
            <th className="px-3 py-2 text-right">Customers</th>
            <th className="px-3 py-2 text-left">Source</th>
            <th className="px-3 py-2 text-right">Ex-GST</th>
            <th className="px-3 py-2 text-right">Amount incl. GST</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isFetching && plans.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-10 text-center text-gray-400">Loading…</td>
            </tr>
          )}
          {plans.map((p) => {
            const [cls, label] = SRC[p.source] || SRC.none;
            const v = draft[p.plan] ?? (p.amountInclGst ?? "");
            return (
              <tr key={p.plan}>
                <td className="max-w-[420px] break-all px-4 py-2 text-xs text-gray-800">{p.plan}</td>
                <td className="px-3 py-2 text-right tabular-nums">{p.customers}</td>
                <td className="px-3 py-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cls}`}>{label}</span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                  {p.amountExGst != null ? inr(p.amountExGst) : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    className={`${fieldClass} w-28 text-right`}
                    style={{ backgroundColor: "#fff", color: "#1f2937" }}
                    value={v}
                    onChange={(e) => setDraft((d) => ({ ...d, [p.plan]: e.target.value }))}
                    onBlur={() => {
                      if (draft[p.plan] !== undefined && String(draft[p.plan]) !== String(p.amountInclGst ?? "")) {
                        save(p, draft[p.plan]);
                      }
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

// ═══════════════════════════════ Rate ═══════════════════════════════════════
const RateTab = () => {
  const dispatch = useDispatch();
  const { data } = useGetNcsIncentiveConfigQuery();
  const [pct, setPct] = useState("");
  const [from, setFrom] = useState(fmtDateInput());

  const save = async () => {
    try {
      await updateIncentiveConfigApi({ pct: Number(pct), effectiveFrom: from });
      dispatch(appApi.util.invalidateTags(["NetplusIncentive"]));
      setPct("");
    } catch (e) {
      Swal.fire({ icon: "error", title: "Could not save", text: errMsg(e) });
    }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
      <section className={`${panelClass} p-5`}>
        <h3 className="text-sm font-semibold text-gray-800">Change incentive %</h3>
        <p className="mt-1 text-xs text-gray-500">
          The new % applies from the date you choose. Earlier days keep the rate they
          were earned at.
        </p>
        <span className={`${labelClass} mt-3`}>New %</span>
        <input
          type="number"
          className={`${fieldClass} w-full`}
          style={{ backgroundColor: "#fff", color: "#1f2937" }}
          value={pct}
          onChange={(e) => setPct(e.target.value)}
        />
        <span className={`${labelClass} mt-3`}>Effective from</span>
        <input
          type="date"
          className={`${fieldClass} w-full`}
          style={{ backgroundColor: "#fff", color: "#1f2937" }}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <button
          className="mt-4 w-full rounded-lg bg-cyan-600 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
          disabled={pct === ""}
          onClick={save}
        >
          Save rate
        </button>
      </section>
      <section className={`${panelClass} p-5`}>
        <h3 className="text-sm font-semibold text-gray-800">Rate history</h3>
        <table className="mt-2 min-w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-gray-500">
            <tr>
              <th className="py-2 text-left">Effective from</th>
              <th className="py-2 text-right">Incentive %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.rates || []).map((r) => (
              <tr key={r.effectiveFrom}>
                <td className="py-2">
                  {new Date(r.effectiveFrom).getUTCFullYear() <= 2000
                    ? "Start"
                    : new Date(r.effectiveFrom).toISOString().slice(0, 10)}
                </td>
                <td className="py-2 text-right font-semibold tabular-nums">{r.pct}%</td>
              </tr>
            ))}
            {!data?.rates?.length && (
              <tr>
                <td className="py-2">Start</td>
                <td className="py-2 text-right font-semibold">14%</td>
              </tr>
            )}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-gray-500">
          GST assumed {data?.gstPct ?? 18}% — used to convert amounts to ex-GST.
        </p>
      </section>
    </div>
  );
};

const TABS = [
  ["dash", "Dashboard"],
  ["plans", "Plan prices"],
  ["rate", "Incentive %"],
];

const NcsIncentive = () => {
  const role = useSelector(selectUserRole);
  const [tab, setTab] = useState("dash");

  if (role !== "super-admin") {
    return (
      <>
        <PageTitle activeMenu="Incentive Revenue" motherMenu="Netplus" />
        <div className={`${panelClass} p-10 text-center`}>
          <h3 className="text-lg font-semibold text-gray-900">Super-admin only</h3>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Incentive Revenue" motherMenu="Netplus" />
      <div className="mb-4 inline-flex rounded-full bg-gray-100 p-1">
        {TABS.map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold ${
              tab === k ? "bg-white text-cyan-700 shadow" : "text-gray-500"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
      {tab === "dash" && <Dashboard />}
      {tab === "plans" && <PlanPrices />}
      {tab === "rate" && <RateTab />}
    </>
  );
};

export default NcsIncentive;
